import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";

const ChatWidget = () => {
  const { t } = useTranslation();
  const [shouldLoad, setShouldLoad] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Defer loading significantly - webchat is non-critical, load very late
  useEffect(() => {
    // Only load when user scrolls near the widget (IntersectionObserver)
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          // User has scrolled to the chat section - now load it
          setTimeout(() => setShouldLoad(true), 500);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' } // Start loading 200px before visible
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    // Fallback: load after 10 seconds if user hasn't scrolled there
    const fallbackTimer = setTimeout(() => {
      if (!shouldLoad) {
        setShouldLoad(true);
        observer.disconnect();
      }
    }, 10000);

    return () => {
      observer.disconnect();
      clearTimeout(fallbackTimer);
    };
  }, [shouldLoad]);

  // Load the script only when shouldLoad is true
  useEffect(() => {
    if (!shouldLoad) return;

    const script = document.createElement("script");
    script.src = "https://cdn.apigateway.co/webchat-client..prod/sdk.js";
    script.setAttribute("data-widget-id", "7fa7d33b-b114-11f0-bcc7-1ebeb8c901ff");
    script.setAttribute("data-embed-mode", "embedded");
    script.setAttribute("data-embed-target", "embedded-widget-container");
    script.async = true;
    script.defer = true;
    
    document.body.appendChild(script);
    
    return () => {
      if (script.parentNode) {
        document.body.removeChild(script);
      }
    };
  }, [shouldLoad]);

  return (
    <section className="section bg-background" ref={containerRef}>
      <div className="container-page">
        <div className="max-w-4xl mx-auto">
          <h2 className="h2 text-center mb-6">
            {t("chatWidget.title")}
          </h2>
          <p className="chat-intro body text-center mb-8">
            {t("chatWidget.description")}
          </p>
          <div
            id="embedded-widget-container" 
            style={{ height: '500px' }}
            className="w-full rounded-lg shadow-lg overflow-hidden"
          >
            {!shouldLoad && (
              <div className="w-full h-full flex items-center justify-center bg-muted/50">
                <div className="text-center">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                  <p className="caption text-muted-foreground">{t('chatLoading')}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ChatWidget;